import { Injectable } from "@angular/core";

import { BaseHandlerErrorMapper } from "@app/core/shared/data/network/base-handler-error.mapper";
import { Observable, catchError, timeout } from "rxjs";
import * as Sentry from "@sentry/angular";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { TranslateService } from "@ngx-translate/core";
import { HandlerParameters } from "@app/core/shared/data/network/base-handler.parameters";
import { VERSION } from "src/autogenerated-version";
import { environment } from "src/environments/environment";
import { Router } from "@angular/router";
import { RoutesSlugs } from "@app/routes-slugs";
import { InfrastructureError, InfrastructureErrorType } from "@app/core/shared/data/network/errors/infrastructure.error";

@Injectable()
export class BaseHandler {
  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
    private router: Router
  ) {
    this.errorHandler = BaseHandlerErrorMapper.handleError;
  }

  errorHandler = BaseHandlerErrorMapper.handleError;

  execute(parameters: HandlerParameters): Observable<any> {
    // Add the language code to the header.
    const browserLanguage = this.translateService.getBrowserLang();
    parameters.header["Language-Code"] =
      this.translateService.currentLang || browserLanguage || "";
    parameters.header["App-Version"] = VERSION;

    const requestOptions = {
      body: parameters.body,
      headers: parameters.header,
      params: parameters.urlParams,
      responseType: parameters.responseType,
    };
    const httpRequest = this.http
      .request(parameters.method, parameters.url, requestOptions);

    return environment.NAME === "PROD"
      ? httpRequest.pipe(
          timeout(10000),
          catchError((errorResponse) =>
            this.handleHttpError(errorResponse, parameters)
          )
        )
      : httpRequest.pipe(
          catchError((errorResponse) =>
            this.handleHttpError(errorResponse, parameters)
          )
        );
  }

  private handleHttpError(
    errorResponse: unknown,
    parameters: HandlerParameters
  ): Observable<never> | Observable<false> {
    if ((errorResponse as { name?: string })?.name === "TimeoutError") {
      this.captureTimeoutError(parameters);
      throw new InfrastructureError(
        InfrastructureErrorType.TimeOutError,
        errorResponse,
        "error_timeout"
      );
    }

    const httpErrorResponse = errorResponse as HttpErrorResponse;

    if (httpErrorResponse.status === 0) {
      this.navigateToNoInternet();
    }

    return this.errorHandler(httpErrorResponse);
  }


  private captureTimeoutError(parameters: HandlerParameters): void {
    Sentry.captureMessage("Backend request timeout", {
      level: "error",
      tags: {
        category: "network",
      },
      extra: {
        requestMethod: parameters.method,
        requestUrl: parameters.url,
      },
    });
  }

  private navigateToNoInternet(): void {
    const targetUrl = `/${RoutesSlugs.NO_INTERNET}`;
    if (this.router.url.startsWith(targetUrl)) {
      return;
    }

    this.router.navigate([targetUrl], {
      queryParams: { returnUrl: this.router.url },
    });
  }
}
